import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import type { BubbleNode, ChainId } from '@pleco/shared';
import { isChainId } from '@pleco/shared';

import { BubbleCanvas } from '../components/BubbleCanvas.js';
import { Legend, MapToolbar, StatusStrip, TimeTravel } from '../components/MapControls.js';
import {
  ClusterList,
  HolderList,
  NodeInspector,
  RiskPanel,
  ScorePanel,
  SupplyPanel,
  TokenHeader,
} from '../components/MapPanels.js';
import { ApiError, api } from '../lib/api.js';
import { isDemo, useChains } from '../lib/chains.js';
import { useMapStore } from '../store/mapStore.js';
import { TokenView } from '../components/token/TokenView.js';
import { LoadingBar } from '../components/LoadingBar.js';
import { TokenLoading } from '../components/token/TokenLoading.js';
import type { CanvasApi } from '../components/BubbleCanvas.js';
import { IconFit, IconList, IconMinus, IconPlus } from '../components/Icons.js';

export function MapPage() {
  const { chain: chainParam, address } = useParams();
  const [search, setSearch] = useSearchParams();
  const canvasApi = useRef<CanvasApi | null>(null);
  const [tab, setTab] = useState<'overview' | 'holders'>('overview');

  const {
    data,
    status,
    history,
    loading,
    error,
    options,
    selected,
    selectedCluster,
    railOpen,
    load,
    select,
    selectCluster,
    setToast,
    setOptions,
    toggleRail,
  } = useMapStore();

  const { defaultChain, capability } = useChains();
  const chain: ChainId = chainParam && isChainId(chainParam) ? chainParam : defaultChain;
  // Solana maps are point-in-time; demos replay a fixture on any chain.
  const showTimeTravel = capability(chain, 'timeTravel') || isDemo(address ?? '');

  useEffect(() => {
    if (!address) return;
    const ts = search.get('t');
    void load(chain, address, ts ? { timestamp: Number(ts) } : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chain, address]);

  // keep the deep link in sync with the time cursor
  useEffect(() => {
    const next = new URLSearchParams(search);
    if (options.timestamp) next.set('t', String(options.timestamp));
    else next.delete('t');
    if (next.toString() !== search.toString()) setSearch(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.timestamp]);

  const highlightIds = useMemo(() => {
    if (selectedCluster) return new Set(selectedCluster.members);
    return undefined;
  }, [selectedCluster]);

  const onSelect = useCallback((n?: BubbleNode) => select(n), [select]);

  const share = async () => {
    if (!data || !address) return;
    try {
      const { id } = await api.saveSnapshot(chain, address);
      const url = `${window.location.origin}/map/${chain}/${address}?snapshot=${id}`;
      await navigator.clipboard.writeText(url);
      setToast('Link copied — anyone with access can open this map');
    } catch {
      await navigator.clipboard.writeText(`${window.location.origin}/map/${chain}/${address}`).catch(() => undefined);
      setToast('Link copied');
    }
  };

  const watch = async () => {
    if (!data || !address) return;
    try {
      await api.addWatch({ chain, address, symbol: data.token.symbol, alertScoreBelow: 40, alertClusterAbove: 20 });
      setToast(`${data.token.symbol} added to your watchlist`);
    } catch (e) {
      setToast(
        e instanceof ApiError && e.status === 409
          ? `${data.token.symbol} is already on your watchlist`
          : e instanceof Error
            ? e.message
            : 'Could not add to watchlist',
      );
    }
  };

  // Upstream busy (rate limit / overloaded index): try again by itself a few times.
  const retries = useRef(0);
  useEffect(() => void (retries.current = 0), [address]);
  const upstreamBusy = !!error && /429|overloaded|rate|timed? ?out|ETIMEDOUT|fetch failed/i.test(error);
  useEffect(() => {
    if (!upstreamBusy || !address || retries.current >= 3) return;
    const t = setTimeout(() => {
      retries.current++;
      void load(chain, address);
    }, 4000 * (retries.current + 1));
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upstreamBusy, error, address]);

  if (error) {
    const busy = /429|overloaded|rate|timed? ?out|ETIMEDOUT|fetch failed/i.test(error);
    return (
      <div className="app-body">
        <div className="main-col">
          <div className="canvas-empty">
            <div className="tm-state">
              <h2>{busy ? 'The Solana data provider is busy' : "We couldn't map this token"}</h2>
              <p>
                {/not a valid|invalid/i.test(error)
                  ? 'That does not look like a Solana token address. Check it and try again.'
                  : busy
                    ? retries.current < 3
                      ? 'Very large tokens sometimes hit a short limit on the holder index. Trying again automatically…'
                      : 'The holder index is still overloaded for this token. Give it a minute and try again.'
                    : error}
              </p>
              <button className="tm-btn primary" onClick={() => address && void load(chain, address, undefined, true)}>
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Never render another token's data under this address.
  if (!data || (address && data.token.address !== address && !isDemo(address))) {
    if (chain === 'sol' && address && !isDemo(address))
      return (
        <div className="app-body term-body">
          <TokenLoading key={address} address={address} />
        </div>
      );
    return (
      <div className="app-body">
        <div className="main-col">
          <LoadingBar />
          <div className="canvas-empty">
            <div className="tm-state">
              <div className="tm-loader" aria-hidden="true">
                <i />
                <i />
                <i />
              </div>
              <h2>Reading the holders…</h2>
              <p>The map appears in a few seconds. Links between wallets keep filling in after that.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Real Solana tokens get the trading terminal; demos keep the classic map.
  if (chain === 'sol' && address && !isDemo(address)) {
    return (
      <div className="app-body term-body">
        <TokenView data={data} onShare={share} onWatch={watch} />
      </div>
    );
  }

  return (
    <div className="app-body">
      <div className="main-col">
        {data.partial && <LoadingBar progress={status?.progress} label="Still indexing — the map fills in live" />}
        <StatusStrip data={data} />

        <div style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex' }}>
          <BubbleCanvas
            data={data}
            selectedId={selected?.id}
            highlightIds={highlightIds}
            focusMode={options.focusMode}
            showLabels={options.showLabels}
            showInfrastructure={options.contracts}
            onShowInfrastructure={() => setOptions({ contracts: true })}
            onSelect={onSelect}
            onReady={(a) => (canvasApi.current = a)}
          />
          <MapToolbar
            data={data}
            onExport={() => canvasApi.current?.exportPng(`${data.token.symbol}-trapmap.png`, `${data.token.symbol} · TrapMap`)}
            onFit={() => canvasApi.current?.fit()}
            onShare={share}
            onRefresh={() => address && void load(chain, address, undefined, true)}
          />
          <Legend data={data} />
          <div className="tv-zoom" role="group" aria-label="Zoom">
            <button onClick={() => canvasApi.current?.zoomBy(1.35)} aria-label="Zoom in">
              <IconPlus />
            </button>
            <button onClick={() => canvasApi.current?.zoomBy(1 / 1.35)} aria-label="Zoom out">
              <IconMinus />
            </button>
            <button onClick={() => canvasApi.current?.fit()} aria-label="Fit map">
              <IconFit />
            </button>
            <button className="mobile-rail-toggle" onClick={toggleRail} aria-label="Details">
              <IconList />
            </button>
          </div>
        </div>

        {showTimeTravel && <TimeTravel history={history} data={data} />}
      </div>

      <aside className={`rail ${railOpen ? 'open' : ''}`}>
        <TokenHeader data={data} onWatch={watch} />

        {selected ? (
          <NodeInspector node={selected} data={data} onClose={() => select(undefined)} />
        ) : (
          <>
            <div style={{ padding: '12px 16px 0' }}>
              <div className="tabs">
                <button className={tab === 'overview' ? 'active' : ''} onClick={() => setTab('overview')}>
                  Analysis
                </button>
                <button className={tab === 'holders' ? 'active' : ''} onClick={() => setTab('holders')}>
                  Holders
                </button>
              </div>
            </div>

            {tab === 'overview' ? (
              <>
                <ScorePanel data={data} />
                <RiskPanel data={data} />
                <SupplyPanel data={data} />
                <ClusterList
                  data={data}
                  selectedId={selectedCluster?.id}
                  onSelect={(c) => selectCluster(c)}
                />
              </>
            ) : (
              <HolderList data={data} selectedId={undefined} onSelect={(n) => select(n)} />
            )}

            <div className="panel" style={{ borderBottom: 'none' }}>
              <div className="faint" style={{ fontSize: 11, lineHeight: 1.6 }}>
                Snapshot {new Date(data.asOf * 1000).toLocaleString()}
              </div>
            </div>
          </>
        )}
      </aside>
    </div>
  );
}
